import {
  Filter,
  FilterInputType,
  FilterOption,
  FilterOptionValue,
  SelectedFilterOption,
} from 'src/types';
import { getDefaultSelectedFilterValues } from './getDefaultSelectedFilterValues';
import { isSameFilterOption } from './isSameFilterOption';

const getSelectedCheckboxFilterOptions = (filter: Filter) => {
  const selectedCheckboxes = filter.options.reduce(
    (filterOptionsAcc: FilterOptionValue[], filterOption: FilterOption) =>
      filterOption.active ? filterOptionsAcc.concat([filterOption.value]) : filterOptionsAcc,
    [],
  );

  if (selectedCheckboxes.length > 0) {
    // Note: no filterOptionUid as checkboxes do not have these
    return [{ filterUid: filter.uid, filterOptionValue: selectedCheckboxes }];
  }

  return [];
};

const getSelectedRadioFilterOptions = (filter: Filter) => {
  const selectedRadio: FilterOption | undefined = filter.options.find(
    (filterOption: FilterOption) => filterOption.active,
  );

  // Note: no filterOptionUid as radiobuttons do not have these
  return selectedRadio ? [{ filterUid: filter.uid, filterOptionValue: selectedRadio.value }] : [];
};

const getSelectedRangeFilterOptions = (filter: Filter) => {
  const selectedRangeFilterOptions: SelectedFilterOption[] = filter.options.reduce(
    (selectedFilterOptionsAcc: SelectedFilterOption[], filterOption: FilterOption) => {
      if (filterOption.active) {
        return selectedFilterOptionsAcc.concat([
          {
            filterUid: filter.uid,
            filterOptionUid: filterOption.uid,
            filterOptionValue: filterOption.value as string | number,
          },
        ]);
      }
      return selectedFilterOptionsAcc;
    },
    [],
  );

  return selectedRangeFilterOptions;
};

const getSelectedTreeFilterOptions = (filter: Filter) => {
  const selectedIndex = filter.options.findIndex((filterOption) => filterOption.active);

  return selectedIndex !== -1
    ? [
        {
          filterUid: filter.uid,
          filterOptionValue: filter.options[selectedIndex].value,
          filterOptionDepth: selectedIndex,
        },
      ]
    : [];
};

const getActiveSelectedFilterOptions = (filters: Filter[]) =>
  filters.reduce((acc: SelectedFilterOption[], filter: Filter) => {
    const getSelectedFilterOptions = {
      [FilterInputType.Checkbox]: getSelectedCheckboxFilterOptions,
      [FilterInputType.Radio]: getSelectedRadioFilterOptions,
      [FilterInputType.Range]: getSelectedRangeFilterOptions,
      [FilterInputType.Tree]: getSelectedTreeFilterOptions,
    };

    const selectedFilterOptions = getSelectedFilterOptions[filter.type]?.(filter);
    return acc.concat(selectedFilterOptions || []);
  }, []);

// Combine the default selected filter options with the active selected filter options
// Where a filter option exists in both, the active should take precedent
export const getSelectedFilterOptions = (filters: Filter[]) =>
  getDefaultSelectedFilterValues(filters).reduce(
    (acc: SelectedFilterOption[], defaultSelectedFilterOption: SelectedFilterOption) => {
      if (
        !acc.find((activeSelectedFilterOption) =>
          isSameFilterOption(activeSelectedFilterOption, defaultSelectedFilterOption),
        )
      ) {
        return acc.concat([defaultSelectedFilterOption]);
      }

      return acc;
    },
    getActiveSelectedFilterOptions(filters),
  );
